import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Volume2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getEditions, getEditionById } from '../services/api';

const SERIF_FONT = "'Georgia', 'Times New Roman', serif";
const SANS_FONT = "'Inter', 'Helvetica Neue', sans-serif";

const getImageUrl = (item) => {
  if (item?.cover_image) {
    if (item.cover_image.startsWith('/api/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${item.cover_image}`;
    }
    return item.cover_image;
  }
  return null;
};

// ============================================
// FORBES-STYLE EDITORIAL COVER - LOGO CENTRADO GRANDE
// ============================================
const ForbesEditorialCover = ({ edition, coverData = {} }) => {
  const defaultCoverImage = 'https://customer-assets.emergentagent.com/job_influencia-news/artifacts/u329inrx_EBE3390C-EC91-41E5-AC0C-1CCE1D1F5884.jpeg';
  const imageUrl = coverData.coverImage || getImageUrl(edition) || defaultCoverImage;
  
  const content = {
    coverlines: coverData.coverlines || [
      'Cómo la inteligencia artificial está reescribiendo el poder empresarial',
      'Por qué Latinoamérica necesita menos gurús y más arquitectos',
    ],
    mainHeadline: coverData.mainHeadline || 'EL PODER DE DISEÑAR SISTEMAS',
    personName: coverData.personName || 'Diego Urquijo',
    personDescription: coverData.personDescription || 'Arquitecto de sistemas empresariales e inteligencia artificial',
    quote: coverData.quote || '"Las ideas inspiran.\nLos sistemas gobiernan."',
  };
  
  return (
    <div 
      className="relative w-full bg-[#1a1a2e] overflow-hidden"
      style={{ aspectRatio: '3/4' }}
    >
      {/* ===== FOTO DE FONDO ===== */}
      {imageUrl && (
        <div className="absolute inset-0">
          <img
            src={imageUrl}
            alt={content.personName}
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 20%' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/80" />
        </div>
      )}
      
      {/* ===== LOGO CENTRADO GRANDE - ESTILO FORBES ===== */}
      <div className="absolute top-4 left-0 right-0 z-20 text-center">
        <h1 
          className="text-3xl tracking-[0.4em] text-white font-light"
          style={{ fontFamily: SERIF_FONT }}
        >
          INFLUENCIA
        </h1>
        <div className="flex items-center justify-center gap-3 my-1">
          <div className="w-12 h-[1px] bg-white/60" />
          <span className="text-white/60 text-sm">&</span>
          <div className="w-12 h-[1px] bg-white/60" />
        </div>
        <h1 
          className="text-3xl tracking-[0.4em] text-white font-light"
          style={{ fontFamily: SERIF_FONT }}
        >
          PODER
        </h1>
      </div>
      
      {/* ===== COVERLINES - ESQUINA SUPERIOR DERECHA ===== */}
      <div className="absolute top-28 right-3 z-20 text-right max-w-[50%]">
        {content.coverlines.map((line, i) => (
          <p 
            key={i}
            className="text-[9px] text-white/80 leading-snug mb-1.5 italic"
            style={{ fontFamily: SERIF_FONT }}
          >
            {line}
          </p>
        ))}
      </div>
      
      {/* ===== CONTENIDO INFERIOR ===== */}
      <div className="absolute bottom-0 left-0 right-0 z-20 p-4">
        {/* CITA */}
        <p 
          className="text-[11px] text-white/90 leading-relaxed mb-2 whitespace-pre-line"
          style={{ fontFamily: SERIF_FONT }}
        >
          {content.quote}
        </p>
        
        {/* NOMBRE */}
        <h2 
          className="text-xl text-white tracking-wide mb-0.5 font-normal"
          style={{ fontFamily: SERIF_FONT }}
        >
          {content.personName}
        </h2>
        
        {/* DESCRIPCIÓN */}
        <p 
          className="text-[10px] text-white/70 mb-3 leading-relaxed"
          style={{ fontFamily: SANS_FONT }}
        >
          {content.personDescription}
        </p>
        
        {/* TITULAR PRINCIPAL */}
        <h1 
          className="text-xl text-white font-bold leading-tight tracking-tight uppercase"
          style={{ fontFamily: SERIF_FONT }}
        >
          {content.mainHeadline}
        </h1>
      </div>
    </div>
  );
};

// ============================================
// EDITIONS LIST PAGE
// ============================================
export const EditionsListPage = () => {
  const [editions, setEditions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getEditions();
        setEditions(data || []);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white/50">Cargando...</div>
      </div>
    );
  }

  // Get cover data from edition or use defaults
  const getCoverData = (edition) => {
    if (edition?.cover_data) {
      return {
        coverImage: edition.cover_data.coverImage || edition.cover_image,
        coverlines: edition.cover_data.coverlines || [],
        mainHeadline: edition.cover_data.mainHeadline || edition.title?.toUpperCase() || '',
        personName: edition.cover_data.personName || '',
        personDescription: edition.cover_data.personDescription || '',
        quote: edition.cover_data.quote || '',
      };
    }
    // Default fallback
    return {
      coverImage: edition?.cover_image || '',
      coverlines: [],
      mainHeadline: edition?.title?.toUpperCase() || '',
      personName: '',
      personDescription: '',
      quote: '',
    };
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header minimalista */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-5">
          <h1 
            className="text-xl text-white/70 tracking-wide font-light"
            style={{ fontFamily: SERIF_FONT }}
          >
            La Edición
          </h1>
        </div>
      </div>

      {/* Portada Principal */}
      {editions[0] && (
        <div className="max-w-7xl mx-auto px-4 py-8 lg:py-16">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Portada Forbes */}
            <div className="flex justify-center">
              <Link 
                to={`/edition/${editions[0].edition_id}`} 
                className="block w-full max-w-sm shadow-2xl hover:shadow-white/5 transition-shadow duration-500"
              >
                <ForbesEditorialCover 
                  edition={editions[0]} 
                  coverData={getCoverData(editions[0])}
                />
              </Link>
            </div>

            {/* Info - Minimalista */}
            <div className="text-center lg:text-left">
              {/* EDICIÓN ACTUAL - En rojo */}
              <span 
                className="text-xs tracking-[0.3em] mb-4 inline-block"
                style={{ color: '#E31B23', fontFamily: SANS_FONT }}
              >
                EDICIÓN ACTUAL
              </span>
              
              <h2 
                className="text-2xl lg:text-3xl text-white mb-4 leading-relaxed font-light"
                style={{ fontFamily: SERIF_FONT }}
              >
                {editions[0].title}
              </h2>
              
              <p 
                className="text-white/40 text-sm mb-8 max-w-md leading-relaxed"
                style={{ fontFamily: SANS_FONT }}
              >
                {editions[0].summary || 'Una mirada profunda al liderazgo y la arquitectura de sistemas en la era digital.'}
              </p>
              
              {/* Secciones */}
              {editions[0].sections?.length > 0 && (
                <div className="mb-8">
                  <p 
                    className="text-[10px] text-white/30 tracking-[0.3em] mb-3"
                    style={{ fontFamily: SANS_FONT }}
                  >
                    EN ESTA EDICIÓN
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
                    {editions[0].sections.slice(0, 3).map((section, i) => (
                      <span 
                        key={i} 
                        className="text-xs text-white/50 border border-white/20 px-3 py-1"
                        style={{ fontFamily: SANS_FONT }}
                      >
                        {section.section_type?.replace('_', ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <Link to={`/edition/${editions[0].edition_id}`}>
                <Button 
                  className="bg-white text-black hover:bg-white/90 min-h-[44px] px-8 text-sm tracking-wide"
                  style={{ fontFamily: SANS_FONT }}
                >
                  Leer Edición
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Ediciones Anteriores */}
      {editions.length > 1 && (
        <div className="max-w-7xl mx-auto px-4 py-8 lg:py-16 border-t border-white/10">
          <h2 
            className="text-base text-white/50 mb-8 tracking-wide font-light"
            style={{ fontFamily: SERIF_FONT }}
          >
            Ediciones Anteriores
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6">
            {editions.slice(1, 9).map((edition, i) => (
              <Link
                key={edition.edition_id}
                to={`/edition/${edition.edition_id}`}
                className="block group"
              >
                <div className="relative aspect-[3/4] bg-[#0a0a0a] overflow-hidden">
                  {getImageUrl(edition) ? (
                    <>
                      <img 
                        src={getImageUrl(edition)}
                        alt={edition.title}
                        className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-white/20 text-xl" style={{ fontFamily: SERIF_FONT }}>
                        {edition.week_number}
                      </span>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <p 
                      className="text-white text-xs font-medium line-clamp-2"
                      style={{ fontFamily: SERIF_FONT }}
                    >
                      {edition.title}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {editions.length === 0 && (
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <p className="text-white/30" style={{ fontFamily: SANS_FONT }}>No hay ediciones disponibles</p>
        </div>
      )}
    </div>
  );
};

// ============================================
// EDITION DETAIL PAGE
// ============================================
export const EditionDetailPage = () => {
  const { editionId } = useParams();
  const [edition, setEdition] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEdition = async () => {
      try {
        const data = await getEditionById(editionId);
        setEdition(data);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEdition();
  }, [editionId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white/50">Cargando...</div>
      </div>
    );
  }

  if (!edition) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white/30">Edición no encontrada</div>
      </div>
    );
  }

  const sectionLabels = {
    cover_story: 'Historia de Portada',
    entrevista_exclusiva: 'Entrevista',
    análisis: 'Análisis',
    impacto_latam: 'Impacto',
    opinion: 'Opinión',
    tendencias: 'Tendencias',
  };

  // Get cover data from edition or use defaults
  const getCoverData = (ed) => {
    if (ed?.cover_data) {
      return {
        coverImage: ed.cover_data.coverImage || ed.cover_image,
        coverlines: ed.cover_data.coverlines || [],
        mainHeadline: ed.cover_data.mainHeadline || ed.title?.toUpperCase() || '',
        personName: ed.cover_data.personName || '',
        personDescription: ed.cover_data.personDescription || '',
        quote: ed.cover_data.quote || '',
      };
    }
    return {
      coverImage: ed?.cover_image || '',
      coverlines: [],
      mainHeadline: ed?.title?.toUpperCase() || '',
      personName: '',
      personDescription: '',
      quote: '',
    };
  };

  const coverData = getCoverData(edition);

  return (
    <div className="min-h-screen bg-black">
      {/* Breadcrumb */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Link 
            to="/edition" 
            className="flex items-center gap-2 text-sm text-white/40 hover:text-white transition-colors"
            style={{ fontFamily: SANS_FONT }}
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Link>
        </div>
      </div>

      {/* Header con Portada */}
      <div className="max-w-7xl mx-auto px-4 py-8 lg:py-16">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-start">
          {/* Portada */}
          <div className="w-full max-w-xs flex-shrink-0">
            <ForbesEditorialCover edition={edition} coverData={coverData} />
          </div>

          {/* Info */}
          <div className="flex-1 pt-4">
            <span 
              className="text-xs tracking-[0.3em]"
              style={{ color: '#E31B23', fontFamily: SANS_FONT }}
            >
              EDICIÓN ACTUAL
            </span>
            <h1 
              className="text-3xl lg:text-4xl text-white mt-4 mb-6 leading-relaxed font-light"
              style={{ fontFamily: SERIF_FONT }}
            >
              {edition.title}
            </h1>
            <p 
              className="text-white/40 mb-8 max-w-xl leading-relaxed"
              style={{ fontFamily: SANS_FONT }}
            >
              {edition.summary || 'Una edición dedicada al liderazgo visionario y la arquitectura de sistemas.'}
            </p>
            <div className="flex gap-3">
              {edition.pdf_url && (
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                  <Download className="w-4 h-4 mr-2" /> PDF
                </Button>
              )}
              {edition.audio_url && (
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                  <Volume2 className="w-4 h-4 mr-2" /> Audio
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Secciones */}
      {edition.sections?.length > 0 && (
        <div className="max-w-3xl mx-auto px-4 py-8 lg:py-16 border-t border-white/10">
          <h2 
            className="text-base text-white/50 mb-8 tracking-wide font-light"
            style={{ fontFamily: SERIF_FONT }}
          >
            Contenido
          </h2>
          <div className="space-y-8">
            {edition.sections.map((section, i) => (
              <article key={i} className="border-b border-white/10 pb-8">
                <span 
                  className="text-[10px] tracking-[0.3em]"
                  style={{ color: '#E31B23', fontFamily: SANS_FONT }}
                >
                  {sectionLabels[section.section_type] || section.section_type?.replace('_', ' ')}
                </span>
                <h3 
                  className="text-xl lg:text-2xl text-white mt-2 mb-4 font-light"
                  style={{ fontFamily: SERIF_FONT }}
                >
                  {section.title}
                </h3>
                <p 
                  className="text-white/40 leading-relaxed mb-3"
                  style={{ fontFamily: SANS_FONT }}
                >
                  {section.content}
                </p>
                <p 
                  className="text-xs text-white/30"
                  style={{ fontFamily: SANS_FONT }}
                >
                  Por {section.author}
                </p>
              </article>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EditionsListPage;
